import React, { useEffect, useState } from "react";
import {
  Page,
  LegacyCard,
  IndexTable,
  Text,
  Badge,
  Button,
  Filters,
  Spinner,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { formatPrice, formatFriendlyDate, formatPlate } from "../../utility/funcs";

function Infractions() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all"); // all | paid | unpaid
  const [queryValue, setQueryValue] = useState("");

  const fetchData = async () => {
    setLoading(true);
    let q = supabase
      .from("infractions")
      .select(
        "id, infraction_date, plate, amount, reason, paid, paid_at, customer_id, company_id, reservation_id, customer:customer_id(id, first_name, last_name), company:company_id(id, name), reservation:reservation_id(id)"
      )
      .order("infraction_date", { ascending: false });
    if (statusFilter === "paid") q = q.eq("paid", true);
    else if (statusFilter === "unpaid") q = q.eq("paid", false);
    if (queryValue.trim()) {
      q = q.ilike("plate", `%${queryValue.trim()}%`);
    }
    const { data, error } = await q;
    if (error) console.error(error);
    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, queryValue]);

  const resourceName = { singular: "infracción", plural: "infracciones" };

  const rowMarkup = rows.map((r, idx) => (
    <IndexTable.Row
      id={String(r.id)}
      key={r.id}
      position={idx}
      onClick={() => navigate(`/infractions/${r.id}`)}
    >
      <IndexTable.Cell>
        <Text variant="bodySm">{formatFriendlyDate(r.infraction_date)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm" fontWeight="semibold">{formatPlate(r.plate)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm">
          {r.customer
            ? `${r.customer.first_name || ""} ${r.customer.last_name || ""}`.trim()
            : r.company
              ? r.company.name
              : "—"}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm">
          {r.reservation_id ? `#${r.reservation_id}` : "—"}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm">{formatPrice(r.amount)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm" tone="subdued">{r.reason}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={r.paid ? "success" : "warning"}>
          {r.paid ? "Pagada" : "Pendiente"}
        </Badge>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const filters = [
    {
      key: "status",
      label: "Estado",
      filter: (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            pressed={statusFilter === "all"}
            onClick={() => setStatusFilter("all")}
          >
            Todas
          </Button>
          <Button
            pressed={statusFilter === "unpaid"}
            onClick={() => setStatusFilter("unpaid")}
          >
            Pendientes
          </Button>
          <Button
            pressed={statusFilter === "paid"}
            onClick={() => setStatusFilter("paid")}
          >
            Pagadas
          </Button>
        </div>
      ),
      shortcut: true,
    },
  ];

  return (
    <AppWrapper>
      <Page
        title="Multas / Infracciones"
        primaryAction={{
          content: "Nueva Infracción",
          onAction: () => navigate("/infractions/new"),
        }}
      >
        <LegacyCard>
          <div style={{ padding: 12 }}>
            <Filters
              queryValue={queryValue}
              queryPlaceholder="Buscar por placa..."
              onQueryChange={setQueryValue}
              onQueryClear={() => setQueryValue("")}
              filters={filters}
              onClearAll={() => {
                setQueryValue("");
                setStatusFilter("all");
              }}
            />
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spinner size="small" />
            </div>
          ) : (
            <IndexTable
              resourceName={resourceName}
              itemCount={rows.length}
              selectable={false}
              headings={[
                { title: "Fecha" },
                { title: "Placa" },
                { title: "Cliente" },
                { title: "Reserva" },
                { title: "Monto" },
                { title: "Motivo" },
                { title: "Estado" },
              ]}
            >
              {rowMarkup}
            </IndexTable>
          )}
        </LegacyCard>
      </Page>
    </AppWrapper>
  );
}

export default Infractions;
